import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/shared/store/store';
import { initializeAuth } from '@/features/security/sagas/auth-slice';
import { adminInitializeAuth } from '@/features/security/sagas/admin-slice';
import { sessionService } from '@/shared/services/session.service';
import { initializePlanLimits } from '@/features/admin/utils/plan-limits';

/**
 * Componente que inicializa la sesión del usuario
 * desde cookies/localStorage al cargar la aplicación.
 *
 * NOTA: El manejo de tokens OAuth (Google, Facebook) se hace en OAuthCallbackHandler
 */
const SessionInitializer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // Limpiar cookies corruptas antes de inicializar
    sessionService.cleanCorruptCookies();

    // Inicializar sesión de usuario normal
    dispatch(initializeAuth());

    // Inicializar sesión de admin
    dispatch(adminInitializeAuth());

    // Inicializar límites de planes — SOLO con sesión.
    //
    // Este componente se monta en la raíz (App.tsx), o sea también en rutas
    // PÚBLICAS (/login, /register). `initializePlanLimits` pega a /plans/limits
    // vía `adminApi`, y el interceptor 401 de esa instancia hace
    // clearSession() + redirect a /login. Resultado medido en producción
    // (2026-08-08): un visitante anónimo que abría /register era expulsado a
    // /login antes de poder ver el formulario — el alta quedaba cerrada.
    //
    // Los límites de plan sólo se usan estando adentro, así que sin token no
    // hay nada que precargar.
    if (sessionService.getToken()) {
      initializePlanLimits().catch(() => {
        // Silente a propósito: sin límites precargados el consumidor usa sus
        // valores por defecto (ver getPlanLimitsFromAPI).
      });
    }

    // No necesitamos manejar WebSocket aquí; las sagas de notificaciones
    // se encargan de conectar cuando la sesión está lista
  }, [dispatch]);

  // Este componente no renderiza nada, solo inicializa la sesión
  return null;
};

export default SessionInitializer;
